import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import {
  FiMapPin,
  FiNavigation,
  FiClock,
  FiWifi,
  FiWifiOff,
  FiBattery,
  FiSpeed,
  FiRefreshCw,
  FiSettings,
  FiAlertCircle,
  FiCheckCircle,
  FiPlay,
  FiPause,
  FiMaximize2,
  FiMinimize2,
  FiDownload,
  FiFilter,
  FiSearch,
  FiCalendar,
  FiTrendingUp,
  FiActivity,
  FiX
} from 'react-icons/fi';

const GpsTracking = () => {
  const { user } = useAuth();
  const [hasGpsAccess, setHasGpsAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [cars, setCars] = useState([]);
  const [gpsPositions, setGpsPositions] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [gpsHistory, setGpsHistory] = useState([]);
  const [gpsStats, setGpsStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(10000); // 10 secondes
  const [mapView, setMapView] = useState('satellite'); // satellite, street, hybrid
  const [showOfflineCars, setShowOfflineCars] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all'); // all, online, offline
  const [searchTerm, setSearchTerm] = useState('');
  const [timeRange, setTimeRange] = useState('24h'); // 1h, 24h, 7d, 30d
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapRef = useRef(null);
  const refreshTimerRef = useRef(null);

  useEffect(() => {
    checkGpsAccess();
  }, []);

  useEffect(() => {
    if (hasGpsAccess) {
      fetchCars();
      fetchGpsPositions();
      if (autoRefresh) {
        startAutoRefresh();
      }
      return () => {
        if (refreshTimerRef.current) {
          clearInterval(refreshTimerRef.current);
        }
      };
    }
  }, [hasGpsAccess, autoRefresh, refreshInterval]);

  const checkGpsAccess = async () => {
    try {
      setCheckingAccess(true);
      const response = await api.get('/api/users/me');
      if (response.data.data?.canAccessGpsTracking) {
        setHasGpsAccess(true);
      } else {
        setHasGpsAccess(false);
      }
    } catch (error) {
      console.error('Error checking GPS access:', error);
      setHasGpsAccess(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  const fetchCars = async () => {
    try {
      const response = await api.get('/api/cars');
      setCars(response.data.data);
    } catch (error) {
      console.error('Error fetching cars:', error);
      toast.error('Erreur lors de la récupération des voitures');
    }
  };

  const fetchGpsPositions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/gps/current-positions');
      setGpsPositions(response.data.data);
    } catch (error) {
      console.error('Error fetching GPS positions:', error);
      toast.error('Erreur lors de la récupération des positions GPS');
    } finally {
      setLoading(false);
    }
  };

  const fetchGpsHistory = async (carId, period = timeRange) => {
    try {
      const response = await api.get(`/api/gps/history/${carId}?period=${period}`);
      setGpsHistory(response.data.data);
    } catch (error) {
      console.error('Error fetching GPS history:', error);
      toast.error('Erreur lors de la récupération de l\'historique GPS');
    }
  };

  const fetchGpsStats = async (carId, period = timeRange) => {
    try {
      const response = await api.get(`/api/gps/stats/${carId}?period=${period}`);
      setGpsStats(response.data.data);
    } catch (error) {
      console.error('Error fetching GPS stats:', error);
      toast.error('Erreur lors de la récupération des statistiques GPS');
    }
  };

  const startAutoRefresh = () => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }
    refreshTimerRef.current = setInterval(() => {
      fetchGpsPositions();
    }, refreshInterval);
  };

  const handleCarSelect = async (car) => {
    setSelectedCar(car);
    if (car.hasGpsData) {
      await Promise.all([
        fetchGpsHistory(car.carId),
        fetchGpsStats(car.carId)
      ]);
    }
  };

  const handleTestConnection = async (carId) => {
    try {
      const response = await api.post('/api/gps/test-connection', { carId });
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error testing GPS connection:', error);
      toast.error('Erreur lors du test de connexion GPS');
    }
  };

  const toggleAutoRefresh = () => {
    if (autoRefresh) {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
      setAutoRefresh(false);
    } else {
      setAutoRefresh(true);
      startAutoRefresh();
    }
  };

  const handleRefresh = () => {
    fetchGpsPositions();
    if (selectedCar) {
      fetchGpsHistory(selectedCar.carId);
      fetchGpsStats(selectedCar.carId);
    }
  };

  const filteredPositions = gpsPositions.filter(position => {
    const matchesSearch = position.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
      position.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      position.licensePlate.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'online' && position.isOnline) ||
      (filterStatus === 'offline' && !position.isOnline);

    const matchesOffline = showOfflineCars || position.isOnline;

    return matchesSearch && matchesStatus && matchesOffline;
  });

  const getStatusColor = (isOnline) => {
    return isOnline ? 'text-brand-green' : 'text-red-600';
  };

  const getStatusIcon = (isOnline) => {
    return isOnline ? <FiWifi className="w-4 h-4" /> : <FiWifiOff className="w-4 h-4" />;
  };

  const formatSpeed = (speed) => {
    return speed ? `${Math.round(speed)} km/h` : 'N/A';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffMins < 1440) return `Il y a ${Math.floor(diffMins / 60)}h`;
    return `Il y a ${Math.floor(diffMins / 1440)}j`;
  };

  if (checkingAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
          <FiMapPin className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 text-primary-600" />
        </div>
        <p className="text-gray-500 dark:text-gray-400 animate-pulse">Vérification de l'accès GPS...</p>
      </div>
    );
  }

  if (!hasGpsAccess) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-sm border border-gray-100 dark:border-gray-700 text-center max-w-md">
          <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-rose-100 dark:from-red-900/40 dark:to-rose-900/40 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FiMapPin className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Accès GPS Refusé</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Votre agence n'a pas encore reçu l'autorisation d'accéder au suivi GPS.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Contactez le superadmin pour demander l'accès au suivi GPS de vos véhicules.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-gradient-to-br from-primary-500 to-indigo-600 rounded-lg sm:rounded-xl shadow-lg">
              <FiMapPin className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
            </div>
            <span className="hidden sm:inline">Suivi GPS en Temps Réel</span>
            <span className="sm:hidden">Suivi GPS</span>
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Surveillez vos véhicules en temps réel
          </p>
        </div>

        <div className="flex gap-2 sm:gap-3 flex-wrap">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-all disabled:opacity-50 text-sm"
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Actualiser</span>
          </button>

          <button
            onClick={toggleAutoRefresh}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 font-medium rounded-xl transition-all text-sm ${autoRefresh
              ? 'bg-gradient-to-r from-brand-green to-emerald-600 text-white shadow-lg shadow-brand-green/25'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
          >
            {autoRefresh ? <FiPause className="w-4 h-4" /> : <FiPlay className="w-4 h-4" />}
            {autoRefresh ? 'Pause' : 'Auto'}
          </button>

          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-all text-sm"
          >
            {isFullscreen ? <FiMinimize2 className="w-4 h-4" /> : <FiMaximize2 className="w-4 h-4" />}
            <span className="hidden sm:inline">{isFullscreen ? 'Réduire' : 'Plein écran'}</span>
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher une voiture..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-gray-900 dark:text-white cursor-pointer"
            >
              <option value="all">Tous les statuts</option>
              <option value="online">En ligne</option>
              <option value="offline">Hors ligne</option>
            </select>
          </div>

          {/* Time Range */}
          <div>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-gray-900 dark:text-white cursor-pointer"
            >
              <option value="1h">1 heure</option>
              <option value="24h">24 heures</option>
              <option value="7d">7 jours</option>
              <option value="30d">30 jours</option>
            </select>
          </div>
        </div>
      </div>

      {/* GPS Status Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Véhicules</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{gpsPositions.length}</p>
            </div>
            <div className="w-14 h-14 bg-gradient-to-br from-primary-100 to-indigo-100 dark:from-primary-900/40 dark:to-indigo-900/40 rounded-2xl flex items-center justify-center">
              <FiMapPin className="w-7 h-7 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">En Ligne</p>
              <p className="text-3xl font-bold text-brand-green mt-1">
                {gpsPositions.filter(p => p.isOnline).length}
              </p>
            </div>
            <div className="w-14 h-14 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40 rounded-2xl flex items-center justify-center">
              <FiWifi className="w-7 h-7 text-brand-green" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Hors Ligne</p>
              <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-1">
                {gpsPositions.filter(p => !p.isOnline).length}
              </p>
            </div>
            <div className="w-14 h-14 bg-gradient-to-br from-red-100 to-rose-100 dark:from-red-900/40 dark:to-rose-900/40 rounded-2xl flex items-center justify-center">
              <FiWifiOff className="w-7 h-7 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Avec GPS</p>
              <p className="text-3xl font-bold text-primary-600 dark:text-primary-400 mt-1">
                {gpsPositions.filter(p => p.hasGpsData).length}
              </p>
            </div>
            <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-violet-100 dark:from-purple-900/40 dark:to-violet-900/40 rounded-2xl flex items-center justify-center">
              <FiNavigation className="w-7 h-7 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vehicle List */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
                  <FiNavigation className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Véhicules</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{filteredPositions.length} véhicule{filteredPositions.length > 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>

            <div className="max-h-[400px] overflow-y-auto">
              {filteredPositions.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <FiMapPin className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 font-medium">Aucun véhicule trouvé</p>
                </div>
              ) : (
                filteredPositions.map((position) => (
                  <div
                    key={position.carId}
                    onClick={() => handleCarSelect(position)}
                    className={`p-4 border-b border-gray-50 dark:border-gray-700/50 cursor-pointer transition-all duration-200 ${selectedCar?.carId === position.carId
                      ? 'bg-gradient-to-r from-primary-50 to-indigo-50 dark:from-primary-900/20 dark:to-indigo-900/10 border-l-4 border-l-primary-500'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {position.brand} {position.model}
                      </h4>
                      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${position.isOnline
                        ? 'bg-brand-green/10 text-brand-green'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                        }`}>
                        {getStatusIcon(position.isOnline)}
                        <span>{position.isOnline ? 'En ligne' : 'Hors ligne'}</span>
                      </div>
                    </div>

                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 font-mono">
                      {position.licensePlate}
                    </p>

                    {position.hasGpsData ? (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400">Vitesse:</span>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">{formatSpeed(position.speed)}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400">Dernière mise à jour:</span>
                          <span className="font-medium text-gray-600 dark:text-gray-400">{formatTimestamp(position.timestamp)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Pas de tracker GPS</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTestConnection(position.carId);
                          }}
                          className="px-3 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                          Tester
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Map and Details */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-primary-50 to-indigo-50 dark:from-primary-900/10 dark:to-indigo-900/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
                  <FiMapPin className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  {selectedCar ? `${selectedCar.brand} ${selectedCar.model}` : 'Sélectionnez un véhicule'}
                </h3>
              </div>
            </div>

            <div className="p-5">
              {selectedCar ? (
                selectedCar.hasGpsData ? (
                  <div className="space-y-5">
                    {/* Map Placeholder */}
                    <div className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-2xl h-64 flex items-center justify-center border border-gray-200 dark:border-gray-600">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-white dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-sm">
                          <FiMapPin className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 font-medium">Carte GPS</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1 font-mono">
                          {selectedCar.latitude}, {selectedCar.longitude}
                        </p>
                      </div>
                    </div>

                    {/* GPS Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Vitesse actuelle</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{formatSpeed(selectedCar.speed)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Direction</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{selectedCar.heading}°</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Statut</span>
                          <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${selectedCar.isOnline
                            ? 'bg-brand-green/10 text-brand-green'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                            }`}>
                            {getStatusIcon(selectedCar.isOnline)}
                            {selectedCar.isOnline ? 'En ligne' : 'Hors ligne'}
                          </span>
                        </div>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Dernière mise à jour</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{formatTimestamp(selectedCar.timestamp)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Device ID</span>
                          <span className="font-mono text-xs text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">{selectedCar.gpsDeviceId}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Coordonnées</span>
                          <span className="font-mono text-xs text-gray-600 dark:text-gray-400">
                            {selectedCar.latitude.toFixed(6)}, {selectedCar.longitude.toFixed(6)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* GPS Stats */}
                    {gpsStats && (
                      <div className="border-t border-gray-100 dark:border-gray-700 pt-5">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <FiActivity className="w-5 h-5 text-primary-600" />
                          Statistiques GPS
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-gradient-to-br from-primary-50 to-indigo-50 dark:from-primary-900/20 dark:to-indigo-900/10 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{gpsStats.totalPoints}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Points GPS</p>
                          </div>
                          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/10 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-brand-green">{gpsStats.averageSpeed}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Vitesse moy. (km/h)</p>
                          </div>
                          <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/10 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{gpsStats.maxSpeed}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Vitesse max (km/h)</p>
                          </div>
                          <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/10 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{gpsStats.totalDistance}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Distance (km)</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/40 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <FiAlertCircle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Aucun tracker GPS installé
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      Ce véhicule n'a pas encore de tracker GPS installé ou configuré.
                    </p>
                    <button
                      onClick={() => handleTestConnection(selectedCar.carId)}
                      className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-medium rounded-xl shadow-lg shadow-primary-500/25 transition-all"
                    >
                      Tester la connexion
                    </button>
                  </div>
                )
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FiMapPin className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Sélectionnez un véhicule
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Choisissez un véhicule dans la liste pour voir ses données GPS.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* GPS History */}
      {selectedCar && gpsHistory.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
                <FiClock className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Historique GPS</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">10 dernières positions</p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Date/Heure
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Coordonnées
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Vitesse
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Direction
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Altitude
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {gpsHistory.slice(0, 10).map((point, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {new Date(point.timestamp).toLocaleString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600 dark:text-gray-400">
                      {point.latitude.toFixed(6)}, {point.longitude.toFixed(6)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{formatSpeed(point.speed)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {point.heading}°
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {point.altitude ? `${Math.round(point.altitude)}m` : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default GpsTracking;
